import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Drawer, List, Box, IconButton, Typography, Divider } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DownloadManualItem from "./DownloadManualItem";
import ClearCacheItem from "./ClearCacheItem";

import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  GroupsRounded,
  SecurityRounded,
  ViewModuleRounded,
  UploadFileRounded,
  PatternRounded,
  VisibilityOffRounded,
  HistoryRounded,
  AdminPanelSettingsRounded,
  PaidRounded,
  ApartmentRounded,
  LockRounded,
  LinkRounded,
  FingerprintRounded,
  CheckCircleRounded,
  VpnKeyRounded,
  RocketLaunchRounded,
} from "@mui/icons-material";

import { fetchPublicTenantInfo, getTenantLogoUrl } from "../../services/tenantService";
import { logout } from "../../services/accountsService";
import Submenu from "./Submenu";
import SidebarNavItem from "./SidebarNavItem";
import { isActivePath } from "./helpers";

// Increased width to accommodate larger and thicker fonts comfortably
const WIDTH_FULL = 370; 
const DEFAULT_LOGO = process.env.PUBLIC_URL + "/assets/logo.png";

// Role-based permissions
const ROLE_SUBMENUS = {
  anonymizer: ["anonymization", "pseudonymization", "validation", "dicom", "profile"],
};

const useTenant = () => {
  const [tenant, setTenant] = useState(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const info = await fetchPublicTenantInfo();
        if (mounted) setTenant(info);
      } catch (e) {
        console.error("Načítanie verejných info o organizácií zlyhalo", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);
  return tenant;
};

const SectionCard = ({ title, items, open, pathname, onItemClick, sx }) => (
  <Box
    sx={{
      mx: 1.1,
      mb: 1.1,
      borderRadius: 2,
      p: 0.75,
      background: (theme) =>
        `linear-gradient(180deg,
          ${alpha(theme.palette.common.white, 0.03)} 0%,
          ${alpha(theme.palette.common.white, 0.015)} 100%)`,
      border: (theme) => `1px solid ${theme.palette.divider}`,
      boxShadow: (theme) => `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.18)}`,
      ...sx,
    }}
  >
    <Typography
      variant="overline"
      sx={{ 
        px: 1.25, 
        pt: 0.75, 
        display: "block", 
        color: "text.secondary",
        fontSize: "0.85rem",
        fontWeight: 700 // Extra thick for section headers
      }}
    >
      {title}
    </Typography>

    <List dense sx={{ pt: 0.25 }}>
      {items.map((it) => (
        <SidebarNavItem
          key={it.key}
          open={open}
          icon={it.icon}
          text={it.text}
          active={isActivePath(pathname, it.path)}
          onClick={() => onItemClick(it)}
        />
      ))}
    </List>
  </Box>
);

const Sidebar = ({ user }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const tenant = useTenant();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  
  const [searchParams] = useSearchParams();
  const shouldAutoOpen = searchParams.get("menu") === "1";

  useEffect(() => {
    if (shouldAutoOpen) setOpen(true);
  }, [shouldAutoOpen]);

  const toggleDrawer = useCallback(() => setOpen((v) => !v), []);

  const handleLogout = useCallback(() => {
    logout().finally(() => navigate("/login"));
  }, [navigate]);

  const onItemClick = useCallback(
    (it) => {
      if (it.action === "logout") return handleLogout();
      if (typeof it.action === "function") return it.action();
      if (it.path) return navigate(it.path);
    },
    [navigate, handleLogout]
  );

  const isSuperuser = user?.is_superuser ?? false;
  const role = user?.profile?.role ?? user?.user_profile?.role ?? user?.role ?? null;
  const isTenantAdmin = role === "admin";

  const baseSubmenus = useMemo(
    () => [
      {
        id: "anonymization",
        title: t("sidebar.sections.anonymization", "Anonymizácia"),
        items: [
          {
            key: "about-app",
            text: t("sidebar.items.about-app", "O aplikácii"),
            icon: <ViewModuleRounded />,
            path: "/about-app",
          },
          {
            key: "pii",
            text: t("sidebar.items.piiAnonymizer", "Anonymizácia osobných údajov"),
            icon: <VisibilityOffRounded />,
            path: "/pii-anonymizer",
          },
          {
            key: "detection",
            text: t("sidebar.items.detection", "AI štúdio"),
            icon: <CheckCircleRounded />,
            path: "/detection",
          },
          {
            key: "uploadedFiles",
            text: t("sidebar.items.anonymizedFiles", "Anonymizované súbory"),
            icon: <UploadFileRounded />,
            path: "/anonymized-files",
          },
          {
            key: "profiles",
            text: t("sidebar.items.anonymizationProfiles", "Profily anonymizácie"),
            icon: <PatternRounded />,
            path: "/anonymization-profiles",
          },
          {
            key: "history",
            text: t("sidebar.items.anonymizationHistory", "História anonymizácie"),
            icon: <HistoryRounded />,
            path: "/pii-history",
          },
        ],
      },
      {
        id: "pseudonymization",
        title: t("sidebar.sections.pseudonymization", "Pseudonymizácia"),
        items: [
          {
            key: "pseudoAnonymizer",
            text: t("sidebar.items.pseudoAnonymization", "Pseudonymizácia"),
            icon: <FingerprintRounded />,
            path: "/pseudo-anonymizer",
          },
          {
            key: "pseudoUploadedFiles",
            text: t("sidebar.items.pseudoAnonymizedFiles", "Pseudonymizované súbory"),
            icon: <UploadFileRounded />,
            path: "/pseudoanonymized-files",
          },
          {
            key: "pseudoProfiles",
            text: t("sidebar.items.pseudoProfiles", "Profily pseudonymizácie"),
            icon: <PatternRounded />,
            path: "/pseudoanonymization-profiles",
          },
        ],
      },
      {
        id: "validation",
        title: t("sidebar.sections.validation", "Validácia"),
        items: [
          {
            key: "piiValidator",
            text: t("sidebar.items.piiValidator", "Validácia osobných údajov"),
            icon: <CheckCircleRounded />,
            path: "/pii-validator",
          },
          {
            key: "validationHistory",
            text: t("sidebar.items.validationHistory", "História validácie"),
            icon: <HistoryRounded />,
            path: "/pii-validation-history",
          },
        ],
      },
      {
        id: "dicom",
        title: t("sidebar.sections.dicom", "DICOM"),
        items: [
          {
            key: "pseudoDicomPseudonymizer",
            text: t("sidebar.items.pseudoDicomPseudonymizer", "Pseudonymizácia DICOM"),
            icon: <FingerprintRounded />,
            path: "/pseudo-dicom-pseudonymizer",
          },
          {
            key: "pseudoDicomDePseudonymizer",
            text: t("sidebar.items.pseudoDicomDePseudonymizer", "Depseudonymizácia DICOM"),
            icon: <VpnKeyRounded />,
            path: "/pseudo-dicom-depseudonymizer",
          },
          {
            key: "pseudoDicomProfiles",
            text: t("sidebar.items.pseudoDicomProfiles", "Profily pseudonymizácie DICOM"),
            icon: <PatternRounded />,
            path: "/pseudo-dicom-profiles",
          },
        ],
      },
      {
        id: "profile",
        title: t("sidebar.sections.profile", "Profil"),
        items: [
          {
            key: "2fa",
            text: t("sidebar.items.twoFactor", "Dvojfaktorové overenie"),
            icon: <SecurityRounded />,
            path: "/2fa-settings",
          },
          {
            key: "changePassword",
            text: t("sidebar.items.changePassword", "Zmena hesla"),
            icon: <LockRounded />,
            path: "/change-password",
          },
        ],
      },
    ],
    [t]
  );

  const adminItems = useMemo(
    () => [
      {
        key: "tenantSettings",
        text: t("sidebar.admin.tenantSettings", "Nastavenia organizácie"),
        icon: <AdminPanelSettingsRounded />,
        path: "/tenant-settings",
      },
      {
        key: "tenantUsers",
        text: t("sidebar.admin.tenantUsers", "Používatelia organizácie"),
        icon: <GroupsRounded />,
        path: "/tenant-users",
      },
      {
        key: "massCreateCredentials",
        text: t("sidebar.admin.massCreateCredentials", "Hromadné vytvorenie prístupov"),
        icon: <GroupsRounded />,
        path: "/mass-create-credentials",
      },
      {
        key: "issueRegistrationLink",
        text: t("sidebar.admin.issueRegistrationLink", "Vygenerovať registračný odkaz"),
        icon: <LinkRounded />,
        path: "/issue-registration-link",
      },
    ],
    [t]
  );

  const superuserItems = useMemo(
    () => [
      {
        key: "onboarding",
        text: t("sidebar.superuser.onboarding", "Onboarding"),
        icon: <RocketLaunchRounded />,
        path: "/admin/onboarding",
      },
      {
        key: "tenants",
        text: t("sidebar.superuser.tenants", "Organizácie"),
        icon: <ApartmentRounded />,
        path: "/admin/superuser-tenants",
      },
      {
        key: "tenantsRegLinks",
        text: t("sidebar.superuser.tenantsRegLinks", "Registračné odkazy organizácií"),
        icon: <ApartmentRounded />,
        path: "/admin/superuser-tenants-reg-links",
      },
      {
        key: "billing",
        text: t("sidebar.superuser.billing", "Fakturácia"),
        icon: <PaidRounded />,
        path: "/admin/billing",
      },
      {
        key: "adminManual",
        text: t("sidebar.superuser.manual", "Manuál správcu"),
        icon: <AdminPanelSettingsRounded />,
        path: "/admin/manual",
      },
    ],
    [t]
  );

  const visibleSubmenus = useMemo(() => {
    let allowedIds;
    
    // 1. Filter by User Role
    if (isSuperuser) {
      allowedIds = ["profile"];
    } else {
      allowedIds = ROLE_SUBMENUS[role];
    }

    let filtered = allowedIds 
      ? baseSubmenus.filter((s) => allowedIds.includes(s.id)) 
      : baseSubmenus;

    // 2. Filter by Tenant Feature Flags
    return filtered.filter((s) => {
      // If it's a DICOM section, only show if flag is true
      if (s.id === "dicom") {
        return tenant?.anon_dicom === true;
      }
      // If it's a Validation section, only show if flag is true
      if (s.id === "validation") {
        return tenant?.anon_validation === true;
      }
      // Everything else (Anonymization, etc.) stays visible by default
      return true;
    });
  }, [isSuperuser, role, baseSubmenus, tenant?.anon_dicom, tenant?.anon_validation]);


  const logoSrc = useMemo(() => {
    const candidate = tenant?.id ? getTenantLogoUrl(tenant.id) : null;
    return candidate ? candidate : DEFAULT_LOGO;
  }, [tenant?.id]);

  const handleLogoError = useCallback((e) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = DEFAULT_LOGO;
  }, []);

  return (
    <Box>
      <IconButton
        onClick={toggleDrawer}
        sx={{
          position: "fixed",
          top: 18,
          left: 12,
          zIndex: 1400,
          width: 36,
          height: 36,
          p: 0.75,
          backdropFilter: "blur(6px)",
          backgroundColor: alpha(theme.palette.background.paper, 0.65),
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.45)},
            inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.18)}`,
          borderRadius: 2,
          "&:hover": {
            backgroundColor: alpha(theme.palette.background.paper, 0.85),
            boxShadow: `0 12px 30px ${alpha(theme.palette.common.black, 0.55)},
              inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`,
          },
        }}
        aria-label={
          open
            ? t("sidebar.actions.closeMenu", "Zavrieť menu")
            : t("sidebar.actions.openMenu", "Otvoriť menu")
        }
      >
        <MenuIcon fontSize="small" />
      </IconButton>

      {open && (
        <Box
          onClick={() => setOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1299,
            backgroundColor: "transparent",
          }}
        />
      )}

      <Drawer
        variant="permanent"
        open={open}
        sx={{
          position: "fixed",
          width: open ? WIDTH_FULL : 0,
          transition: "width 0.22s ease",
          overflowX: "hidden",
          zIndex: 1300,
          "& .MuiDrawer-paper": {
            width: open ? WIDTH_FULL : 0,
            boxSizing: "border-box",
            transition: "width 0.22s ease",
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRight: open ? `1px solid ${theme.palette.divider}` : "none",
            boxShadow: open
              ? "0 20px 60px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.02)"
              : "none",
            backdropFilter: open ? "blur(10px)" : "none",
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
          },
          // GLOBAL FONT OVERRIDES FOR CHILD COMPONENTS (SidebarNavItem, Submenu, etc.)
          "& .MuiListItemText-primary": {
            fontSize: "1.05rem", 
            fontWeight: 600, // Thicker primary text
          },
          "& .MuiListItemText-secondary": {
            fontSize: "0.9rem",
            fontWeight: 500, // Thicker secondary text
          },
          "& .MuiListItemIcon-root svg": {
            fontSize: "1.5rem", 
          }
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 1.5 }}>
          <img
            src={logoSrc}
            alt={t("sidebar.logoAlt", "Logo")}
            onError={handleLogoError}
            style={{ width: open ? 132 : 0, height: "auto", transition: "width .2s ease" }}
          />
        </Box>

        {open && (
          <Box sx={{ px: 2, pb: 0.75 }}>
            <Typography variant="subtitle2" sx={{ color: "text.secondary", lineHeight: 1.05, fontSize: "1rem", fontWeight: 600 }}>
              {t("sidebar.welcome", "Vitajte")}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.2, fontSize: "1.3rem" }}>
              {user?.username ?? t("sidebar.userFallback", "používateľ")}
            </Typography>
          </Box>
        )}

        {isSuperuser && (
          <>
            <Divider sx={{ borderColor: "divider", my: 0.75, mx: 1 }} />
            <SectionCard
              title={t("sidebar.sections.superuser", "Superuser")}
              items={superuserItems}
              open={open}
              pathname={pathname}
              onItemClick={onItemClick}
            />
          </>
        )}

        <List sx={{ px: 0.25, pb: 1 }}>
          {visibleSubmenus.map((s) => (
            <Submenu key={s.id} title={s.title} items={s.items} open={open} navigate={navigate} pathname={pathname} />
          ))}
        </List>

        {isTenantAdmin && (
          <>
            <Divider sx={{ borderColor: "divider", my: 0.75, mx: 1 }} />
            <SectionCard
              title={t("sidebar.sections.administration", "Administrácia")}
              items={adminItems}
              open={open}
              pathname={pathname}
              onItemClick={onItemClick}
            />
          </>
        )}

        <Box sx={{ mt: "auto", width: "100%", p: 1.5 }}>
          <Divider sx={{ borderColor: "divider", mb: 0.75 }} />
          
          <DownloadManualItem user={user} open={open} />

          <ClearCacheItem open={open} />

          <SidebarNavItem
            open={open}
            icon={<LogoutIcon />}
            text={t("sidebar.items.logout", "Odhlásiť sa")}
            active={false}
            onClick={handleLogout}
          />

          {/* Incremented Version Info Here */}
          {open && (
            <Typography
              variant="caption"
              align="center"
              sx={{
                display: "block",
                mt: 1.5,
                mb: 0.5,
                color: "text.disabled",
                letterSpacing: 0.5,
                fontSize: "0.85rem",
                fontWeight: 600, // Make version text a bit more legible too
              }}
            >
              Verzia Cenzor 4.1
              <br />
              Dátum aktualizácie 15.6.2026
            </Typography>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default Sidebar;